/** Retail / wholesale + pack/carton pricing helpers for POS. */

export type PriceMode = 'retail' | 'wholesale';
export type SellUnit = 'base' | 'pack' | 'carton' | 'bag';

export type PackagingPriceSource = {
  price: number;
  wholesale_price?: number | null;
  pack_size?: number | null;
  carton_size?: number | null;
  pack_selling_price?: number | null;
  pack_wholesale_price?: number | null;
  carton_selling_price?: number | null;
  carton_wholesale_price?: number | null;
  bag_size?: number | null;
  bag_selling_price?: number | null;
  bag_wholesale_price?: number | null;
};

export function normalizePackSize(value: number | null | undefined): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 1) return null;
  return Math.floor(n);
}

export function unitsPerSellUnit(product: PackagingPriceSource, sellUnit: SellUnit): number {
  if (sellUnit === 'pack') {
    return normalizePackSize(product.pack_size) || 1;
  }
  if (sellUnit === 'carton') {
    return normalizePackSize(product.carton_size) || 1;
  }
  if (sellUnit === 'bag') {
    return normalizePackSize(product.bag_size) || 1;
  }
  return 1;
}

export function sellUnitLabel(sellUnit: SellUnit): string {
  if (sellUnit === 'pack') return 'Pack';
  if (sellUnit === 'carton') return 'Carton';
  if (sellUnit === 'bag') return 'Bag';
  return 'Pcs';
}

export function availableSellUnits(product: PackagingPriceSource): SellUnit[] {
  const units: SellUnit[] = ['base'];
  if (normalizePackSize(product.pack_size)) units.push('pack');
  if (normalizePackSize(product.carton_size)) units.push('carton');
  if (normalizePackSize(product.bag_size)) units.push('bag');
  return units;
}

function money(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** List price for one sell unit (pcs / pack / carton) in the given price mode. */
export function listPriceFor(
  product: PackagingPriceSource,
  mode: PriceMode,
  sellUnit: SellUnit,
): number {
  const retailBase = Number(product.price) || 0;
  const wholesaleBase =
    product.wholesale_price != null && Number.isFinite(Number(product.wholesale_price))
      ? Number(product.wholesale_price)
      : retailBase;
  const base = mode === 'wholesale' ? wholesaleBase : retailBase;
  const units = unitsPerSellUnit(product, sellUnit);

  if (sellUnit === 'pack') {
    if (mode === 'wholesale' && product.pack_wholesale_price != null) {
      return money(Number(product.pack_wholesale_price));
    }
    if (mode === 'retail' && product.pack_selling_price != null) {
      return money(Number(product.pack_selling_price));
    }
    return money(base * units);
  }

  if (sellUnit === 'carton') {
    if (mode === 'wholesale' && product.carton_wholesale_price != null) {
      return money(Number(product.carton_wholesale_price));
    }
    if (mode === 'retail' && product.carton_selling_price != null) {
      return money(Number(product.carton_selling_price));
    }
    return money(base * units);
  }

  if (sellUnit === 'bag') {
    if (mode === 'wholesale' && product.bag_wholesale_price != null) {
      return money(Number(product.bag_wholesale_price));
    }
    if (mode === 'retail' && product.bag_selling_price != null) {
      return money(Number(product.bag_selling_price));
    }
    return money(base * units);
  }

  return money(base);
}

export function isGstDemoLabel(name?: string | null): boolean {
  return /^GST\s*Demo\b/i.test((name || '').trim());
}

/** Convert cart sell-unit qty into base stock units for inventory/API. */
export function toBaseQuantity(quantity: number, unitsPer: number): number {
  const q = Math.max(0, Math.floor(Number(quantity) || 0));
  const u = Math.max(1, Math.floor(Number(unitsPer) || 1));
  return q * u;
}
